import React, { useState, useRef, useEffect } from 'react';
import { VerificationInputProps } from './VerificationInputProps';
import { WithTranslationProps } from '../Translation/WithTranslationProps';
import withTranslation from 'Common/withTranslation';
import Timer from '../Timer/Timer';

const VerificationInput: React.FC<VerificationInputProps & WithTranslationProps> = ({ verifyCode, t, onResend, onComplete }) => {
  const [values, setValues] = useState<string[]>(Array(6).fill(''));
  const [isValid, setIsValid] = useState<boolean | null>(null); // Track if the code is valid
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const [time, setTime] = useState<number>(600);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [resetTimer, setResetTimer] = useState<boolean>(false);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newValues = [...values];
    const value = e.target.value;

    // Only allow numbers
    if (/^\d$/.test(value) || value === '') {
      newValues[index] = value;
      setValues(newValues);

      // Move to next input if the current one is filled and valid
      if (value && index < 5) {
        inputs.current[index + 1]?.focus();
      }

      // Move to previous input if the current one is empty
      if (!value && index > 0) {
        inputs.current[index - 1]?.focus();
      }

      // Check if all fields are filled
      if (newValues.join('').length === 6) {
        const enteredCode = newValues.join('');
        
        if (enteredCode == verifyCode) {
          setIsValid(true); // Code is correct
          setErrorMessage(null);
        } else {
          setIsValid(false); // Code is incorrect
          setErrorMessage("Register.Verification.NoCodeMatches");
          setValues(Array(6).fill('')); // Reset the values
          inputs.current[0]?.focus(); // Focus on the first input
        }
      }
    }
  };

  // Handle focus on input
  const handleFocus = (index: number) => {
    inputs.current[index]?.select();
  };

  // Trigger onResend when the resend link is clicked
  const handleResend = () => {
    onResend && onResend();    
    setIsTimeout(false);
    setResetTimer(true);
  };

  const onTimeout = () => {
    setValues(Array(6).fill(''));
    setIsTimeout(true);
  }

  const onReseted = () => {
    setResetTimer(false);
    // console.log('reseted');
  }

  useEffect(() => {
    if(isValid) {
        onComplete && onComplete();
    }
  }, [isValid])

  return (
    <div className="flex flex-col justify-center items-center space-y-4 w-full">
      {
        !isTimeout &&
        <>
          <h2 className="text-2xl text-center font-bold text-blue-600">{t("VerificationCode.Title")}</h2>
          <div className="flex justify-center space-x-2">
            {values.map((_, index) => (
              <input
                key={index}
                ref={(el) => (inputs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={values[index]}
                onChange={(e) => handleChange(e, index)}
                onFocus={() => handleFocus(index)}
                className={`w-12 h-12 text-center text-2xl border-2 ${
                  isValid === true
                    ? 'border-green-500'
                    : isValid === false
                    ? 'border-red-500'
                    : 'border-gray-300'
                } focus:outline-none transition-all duration-150 rounded-md`}
              />
            ))}
          </div>
        </>
      }

      {isValid === true && <p className="text-green-500 mt-2">{t('Register.Verification.CodeMatches')}</p>}
      {isValid === false && <p className="text-red-500 mt-2">{t(`${errorMessage}`)}</p>}
      <Timer time={time} reset={resetTimer} onTimeout={onTimeout} onReseted={onReseted}></Timer>
      <button
        className="text-gray-500 italic underline mt-4 cursor-pointer"
        onClick={handleResend}
        type='button'
      >
        {t("VerificationCode.Resend")} {/* Texto de reenvío */}
      </button>
    </div>
  );
};

export default withTranslation(VerificationInput);
